import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore'
import {
    Plus,
    Search,
    Edit,
    Trash2,
    Loader2,
    X,
    Users2
} from 'lucide-react'
import toast from 'react-hot-toast'
import './EquipesPage.css'

export default function EquipesPage() {
    const { t } = useTranslation()
    const { hasPermission } = useAuthStore()

    const [equipes, setEquipes] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')

    const [showModal, setShowModal] = useState(false)
    const [editingEquipe, setEditingEquipe] = useState(null)
    const [modalLoading, setModalLoading] = useState(false)
    const [formData, setFormData] = useState({
        nom_equipe: '',
        description: ''
    })

    useEffect(() => {
        fetchEquipes()
    }, [])

    const fetchEquipes = async () => {
        setLoading(true)
        try {
            const response = await api.get('/equipes', { params: { all: true } })
            setEquipes(response.data.data.equipes)
        } catch (error) {
            toast.error(t('common.error'))
        } finally {
            setLoading(false)
        }
    }

    const openAddModal = () => {
        setEditingEquipe(null)
        setFormData({
            nom_equipe: '',
            description: ''
        })
        setShowModal(true)
    }

    const openEditModal = (equipe) => {
        setEditingEquipe(equipe)
        setFormData({
            nom_equipe: equipe.nom_equipe,
            description: equipe.description || ''
        })
        setShowModal(true)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setModalLoading(true)

        try {
            if (editingEquipe) {
                await api.put(`/equipes/${editingEquipe._id}`, formData)
            } else {
                await api.post('/equipes', formData)
            }
            toast.success(t('common.success'))
            setShowModal(false)
            fetchEquipes()
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.error'))
        } finally {
            setModalLoading(false)
        }
    }

    const handleDelete = async (equipe) => {
        if (!window.confirm(t('equipes.deleteConfirm'))) return

        try {
            await api.delete(`/equipes/${equipe._id}`)
            toast.success(t('common.success'))
            fetchEquipes()
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.error'))
        }
    }

    const filteredEquipes = equipes.filter(e =>
        e.nom_equipe.toLowerCase().includes(searchTerm.toLowerCase())
    )

    return (
        <div className="equipes-page">
            <div className="page-header">
                <div>
                    <h1 className="page-title">{t('equipes.title')}</h1>
                    <p className="page-subtitle">{equipes.length} équipes</p>
                </div>

                {hasPermission('MANAGE_EQUIPES') && (
                    <button className="btn btn-primary" onClick={openAddModal}>
                        <Plus size={18} />
                        {t('equipes.add')}
                    </button>
                )}
            </div>

            {/* Search */}
            <div className="card">
                <div className="filters-bar">
                    <div className="search-form">
                        <div className="search-input-wrapper">
                            <Search size={18} className="search-icon" />
                            <input
                                type="text"
                                className="form-input search-input"
                                placeholder={t('common.search') + '...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Teams Grid */}
            {loading ? (
                <div className="equipes-loading">
                    <Loader2 size={32} className="spin" />
                </div>
            ) : (
                <div className="equipes-grid">
                    {filteredEquipes.map((equipe) => (
                        <div key={equipe._id} className="equipe-card card">
                            <div className="equipe-card-header">
                                <div className="equipe-icon">
                                    <Users2 size={24} />
                                </div>
                                <div className="equipe-info">
                                    <h3>{equipe.nom_equipe}</h3>
                                    {equipe.memberCount !== undefined && (
                                        <span className="member-count">
                                            {equipe.memberCount} {t('equipes.members').toLowerCase()}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {equipe.description && (
                                <p className="equipe-description">{equipe.description}</p>
                            )}

                            <div className="equipe-actions">
                                {hasPermission('MANAGE_EQUIPES') && (
                                    <>
                                        <button
                                            className="btn btn-ghost btn-sm"
                                            onClick={() => openEditModal(equipe)}
                                        >
                                            <Edit size={16} />
                                            {t('common.edit')}
                                        </button>
                                        <button
                                            className="btn btn-ghost btn-sm"
                                            onClick={() => handleDelete(equipe)}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">
                                {editingEquipe ? t('equipes.edit') : t('equipes.add')}
                            </h3>
                            <button className="btn btn-icon btn-ghost" onClick={() => setShowModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="form-label">{t('equipes.name')} *</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={formData.nom_equipe}
                                        onChange={(e) => setFormData({ ...formData, nom_equipe: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t('equipes.description')}</label>
                                    <textarea
                                        className="form-textarea"
                                        rows={3}
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                    {t('common.cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={modalLoading}>
                                    {modalLoading ? <Loader2 size={18} className="spin" /> : t('common.save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}
